import { tx } from "@/i18n/tx";
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, fmtCHF, fmtDate } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { TrendingUp, AlertTriangle, Plus, Sparkles, Loader2 } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, Tooltip, CartesianGrid } from "recharts";
import VatNumberPrompt from "@/components/VatNumberPrompt";
import VerifyBox from "@/components/invoices/VerifyBox";

const PERIODS = [
  { key: "7", label: "7j", days: 7 },
  { key: "30", label: "30j", days: 30 },
  { key: "90", label: "90j", days: 90 },
  { key: "365", label: "12m", days: 365 },
];

export default function Accueil() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [period, setPeriod] = useState("7");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [showCustom, setShowCustom] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      let url = "/sales/summary";
      if (period === "custom" && customStart && customEnd) {
        url += `?start=${customStart}&end=${customEnd}`;
      } else if (period !== "custom") {
        url += `?days=${period}`;
      }
      const { data } = await api.get(url);
      setSummary(data);
    } catch {
      /* silent */
    } finally {
      setSummaryLoading(false);
    }
  }, [period, customStart, customEnd]);

  useEffect(() => {
    let cancelled = false;
    api.get("/invoices").then((i) => { if (!cancelled) setInvoices(i.data); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  const urgent = invoices.filter((i) => ["retard", "bientot_du"].includes(i.status));
  const trialDays = (() => {
    if (!user?.trial_end) return null;
    const ms = new Date(user.trial_end).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
  })();

  const periodLabel = period === "custom"
    ? `${customStart || "…"} → ${customEnd || "…"}`
    : `${PERIODS.find((p) => p.key === period)?.label || period}`;

  // Onboarding checklist: hide once all steps done or dismissed
  const [onboardingDismissed, setOnboardingDismissed] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("oresto-onboarding-dismissed") === "1";
  });
  const dismissOnboarding = () => {
    setOnboardingDismissed(true);
    try { window.localStorage.setItem("oresto-onboarding-dismissed", "1"); } catch {}
  };
  const hasRestaurant = !!(user?.establishment?.name);
  const hasSale = (summary?.total?.total_ttc || 0) > 0 || (summary?.today?.total_ttc || 0) > 0;
  const hasInvoice = (summary?.invoices_total || 0) > 0 || (summary?.pending_invoices || 0) > 0;
  const steps = [
    { done: hasRestaurant, label: tx("ui_accueil.ke75266"), to: "/onboarding" },
    { done: hasSale, label: tx("ui_accueil.k65b39a"), to: "/app/caisse" },
    { done: hasInvoice, label: tx("ui_accueil.k0fb87b"), to: "/app/factures" },
  ];
  const doneCount = steps.filter((s) => s.done).length;
  const showOnboarding = !onboardingDismissed && doneCount < steps.length;

  return (
    <div className="space-y-4">
      <VatNumberPrompt user={user} />
      <VerifyBox />
      {showOnboarding && (
        <div className="card-c relative" style={{ background: "linear-gradient(135deg, rgba(42,125,95,0.08), rgba(200,160,64,0.08))" }} data-testid="onboarding-checklist">
          <button onClick={dismissOnboarding} className="absolute top-3 right-3 text-text-secondary text-xs hover:text-[var(--text)]" data-testid="onboarding-dismiss">{tx("ui_accueil.ke44bd3")}</button>
          <p className="text-xs uppercase tracking-wider text-primary-c font-bold">{tx("ui_accueil.kbc8c64")}</p>
          <p className="text-sm text-text-secondary mt-0.5">Terminez ces {steps.length} étapes pour maîtriser Oresto en 2 minutes.</p>
          <div className="mt-3">
            <div className="flex items-center justify-between text-xs text-text-secondary mb-1">
              <span className="font-semibold">{tx("tpl.done_steps", { count: doneCount, total: steps.length })}</span>
              <span>{Math.round(doneCount / steps.length * 100)}%</span>
            </div>
            <div className="h-1.5 bg-surface-muted rounded-full overflow-hidden">
              <div className="h-full bg-primary-c transition-all" style={{ width: `${doneCount / steps.length * 100}%` }} />
            </div>
          </div>
          <ul className="mt-4 space-y-2">
            {steps.map((s, i) => (
              <li key={i} className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs shrink-0 ${s.done ? "bg-primary-c text-white" : "bg-surface-muted border border-c"}`}>
                    {s.done ? "✓" : i + 1}
                  </span>
                  <span className={`text-sm ${s.done ? "line-through text-text-secondary" : "font-medium"}`}>{s.label}</span>
                </div>
                {!s.done && (
                  <Link to={s.to} className="text-xs font-semibold text-primary-c shrink-0" data-testid={`onboarding-step-${i}`}>{tx("ui_accueil.k6a41e1")}</Link>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="card-c">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-wider text-text-secondary font-semibold">{t("dashboard.cash_today")}</p>
            <p className="font-display text-4xl mt-1" data-testid="today-total">{fmtCHF(summary?.today?.total_ttc || 0)}</p>
            {summary?.today_closed && !summary?.today && <p className="text-xs text-text-secondary mt-1" data-testid="today-closed">{tx("ui_accueil.k7c5817")}</p>}
          </div>
          <Link to="/app/caisse" data-testid="add-sale-link" className="w-12 h-12 rounded-2xl bg-primary-c flex items-center justify-center text-white">
            <Plus className="w-6 h-6" />
          </Link>
        </div>
        {summary?.today && (
          <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
            <div className="bg-surface-muted rounded-xl p-3">
              <p className="text-text-secondary text-xs">{t("dashboard.on_site")}</p>
              <p className="font-semibold">{fmtCHF(summary.today.ca_sur_place)}</p>
            </div>
            <div className="bg-surface-muted rounded-xl p-3">
              <p className="text-text-secondary text-xs">{t("dashboard.takeaway")}</p>
              <p className="font-semibold">{fmtCHF(summary.today.ca_emporter)}</p>
            </div>
          </div>
        )}
      </div>

      <div className="card-c">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-xs uppercase tracking-wider text-text-secondary font-semibold">{t("dashboard.revenue_period")} · {periodLabel}</p>
            <p className="font-display text-2xl">{fmtCHF(summary?.total_period ?? summary?.total_week ?? 0)}</p>
          </div>
          <TrendingUp className="w-5 h-5 text-primary-c" strokeWidth={1.8} />
        </div>

        {/* Period selector */}
        <div className="flex flex-wrap gap-1.5 mb-3" data-testid="period-selector">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => { setPeriod(p.key); setShowCustom(false); }}
              data-testid={`period-btn-${p.key}`}
              className={`px-3 py-1 text-xs font-semibold rounded-full border ${period === p.key ? "bg-primary-c text-white border-transparent" : "bg-surface text-text-secondary border-c"}`}
            >
              {p.label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => { setPeriod("custom"); setShowCustom(true); }}
            data-testid="period-btn-custom"
            className={`px-3 py-1 text-xs font-semibold rounded-full border ${period === "custom" ? "bg-primary-c text-white border-transparent" : "bg-surface text-text-secondary border-c"}`}
          >
            {tx("ui_accueil.kdc877e")}
          </button>
        </div>
        {showCustom && (
          <div className="grid grid-cols-2 gap-2 mb-3" data-testid="period-custom-inputs">
            <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} className="input-c text-sm" data-testid="period-start" placeholder="Du" />
            <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} className="input-c text-sm" data-testid="period-end" placeholder="Au" />
          </div>
        )}

        <div className="h-32 -mx-2 relative">
          {summaryLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/60 z-10 rounded-xl">
              <Loader2 className="w-4 h-4 animate-spin text-primary-c" />
            </div>
          )}
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={summary?.series || []}>
              <defs>
                <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2A7D5F" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#2A7D5F" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#E5E1D8" strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10, fill: "#5C5C5C" }}
                tickFormatter={fmtDate}
                axisLine={false}
                tickLine={false}
                interval={summary?.series?.length > 30 ? Math.floor(summary.series.length / 6) : "preserveStartEnd"}
              />
              <Tooltip
                formatter={(v, _n, item) => {
                  const st = item?.payload?.status;
                  return st === "manquante" ? "Manquante" : st === "ferme" ? tx("ui_accueil.ke7cd58") : st === "a_saisir" ? tx("ui_accueil.kb97ef2") : fmtCHF(v);
                }}
                labelFormatter={fmtDate}
                contentStyle={{ borderRadius: 12, border: "1px solid #E5E1D8" }}
              />
              <Area type="monotone" dataKey="total" stroke="#2A7D5F" strokeWidth={2.5} fill="url(#g1)" connectNulls={false}
                dot={(p) => (p.payload?.status === "manquante"
                  ? <circle key={p.key} cx={p.cx} cy={p.cy ?? 140} r={3.5} fill="#DC2626" />
                  : p.payload?.status === "ferme" ? <circle key={p.key} cx={p.cx} cy={p.cy ?? 140} r={3} fill="#A8A29E" /> : <g key={p.key} />)} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {(() => {
          const miss = (summary?.series || []).filter((x) => x.status === "manquante");
          const closed = (summary?.series || []).filter((x) => x.status === "ferme").length;
          if (!miss.length && !closed) return null;
          return (
            <p className="text-xs mt-2 leading-relaxed" data-testid="chart-status-legend">
              {miss.length > 0 && (
                <Link to="/app/caisse" className="text-red-600 font-semibold" data-testid="missing-days">
                  <span className="inline-block w-2 h-2 rounded-full bg-red-600 mr-1" />
                  {miss.length} caisse{miss.length > 1 ? "s" : ""} manquante{miss.length > 1 ? "s" : ""} : {miss.slice(0, 5).map((x) => fmtDate(x.date)).join(", ")}{miss.length > 5 ? "…" : ""}
                </Link>
              )}
              {miss.length > 0 && closed > 0 && " · "}
              {closed > 0 && <span className="text-text-secondary"><span className="inline-block w-2 h-2 rounded-full bg-stone-400 mr-1" />{tx("plural.closed_days", { count: closed })}</span>}
            </p>
          );
        })()}

        {/* Stats compactes */}
        <div className="grid grid-cols-3 gap-2 mt-3 text-center">
          <div className="bg-surface-muted rounded-xl p-2">
            <p className="text-[10px] uppercase tracking-wider text-text-secondary font-semibold">{tx("ui_accueil.k395aee")}</p>
            <p className="font-display text-sm mt-0.5" data-testid="stat-avg-daily">{fmtCHF(summary?.avg_daily || 0)}</p>
          </div>
          <div className="bg-surface-muted rounded-xl p-2">
            <p className="text-[10px] uppercase tracking-wider text-text-secondary font-semibold">TVA</p>
            <p className="font-display text-sm mt-0.5">{fmtCHF(summary?.total_tva_period ?? summary?.total_tva_week ?? 0)}</p>
          </div>
          <div className="bg-surface-muted rounded-xl p-2">
            <p className="text-[10px] uppercase tracking-wider text-text-secondary font-semibold">{tx("ui_accueil.k8ec8d7")}</p>
            <p className="font-display text-sm mt-0.5" data-testid="stat-best-day">{fmtCHF(summary?.best_day?.total || 0)}</p>
          </div>
        </div>
      </div>


      {urgent.length > 0 && (
        <div className="card-c">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-[#D97706]" strokeWidth={2} />
            <p className="font-semibold">{t("dashboard.invoices_todo")}</p>
          </div>
          <ul className="divide-y divide-[color:var(--border-c)]">
            {urgent.slice(0, 4).map((inv) => (
              <li key={inv.id} className="py-3 flex items-center justify-between">
                <div>
                  <p className="font-medium">{inv.supplier}</p>
                  <p className="text-xs text-text-secondary">Échéance {fmtDate(inv.due_date)}</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold">{fmtCHF(inv.amount)}</p>
                  <span className={`status-badge ${inv.status === "retard" ? "status-retard" : "status-bientot"}`}>
                    {inv.status === "retard" ? "En retard" : tx("ui_accueil.kae53a6")}
                  </span>
                </div>
              </li>
            ))}
          </ul>
          <Link to="/app/factures" className="block mt-3 text-sm font-semibold text-primary-c text-center" data-testid="see-all-invoices-link">{tx("ui_accueil.k9d77f6")}</Link>
        </div>
      )}
    </div>
  );
}
